import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminSidebar from "../../components/admin/AdminSidebar";
import { 
  Check, 
  Trash2, 
  ShieldCheck, 
  Clock, 
  X, 
  Phone, 
  Mail, 
  Tag, 
  MapPin, 
  Calendar,
  Eye,
  RefreshCcw,
  MessageSquare
} from "lucide-react";
import { useModal } from "../../context/ModalContext";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function AdminPosts() {
  const { showAlert, showConfirm } = useModal();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewPost, setViewPost] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/admin/posts`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
      setPosts(res.data);
    } catch (err) {
      console.error("Failed to fetch posts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleView = async (id) => {
    setViewLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/api/admin/posts/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
      setViewPost(res.data);
    } catch (err) {
      showAlert("Failed to fetch post details", "error");
    } finally {
      setViewLoading(false);
    }
  };

  const handleRepost = async (id) => {
    try {
      await axios.put(`${apiUrl}/api/admin/posts/${id}/unhide`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
      fetchPosts();
      if (viewPost) setViewPost(null);
    } catch (err) {
      showAlert("Failed to repost item", "error");
    }
  };

  const handleMessageSeller = async (postId, sellerId) => {
    try {
      const res = await axios.post(`${apiUrl}/api/conversations`, { postId, sellerId }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
      navigate("/admin/messages");
    } catch (err) {
      showAlert("Failed to start conversation", "error");
    }
  };

  const handleDelete = async (id) => {
    showConfirm("Hide this post? The post will no longer be visible to customers.", async () => {
      try {
        await axios.delete(`${apiUrl}/api/admin/posts/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
          },
        });
        fetchPosts();
      } catch (err) {
        showAlert("Failed to hide post", "error");
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <main className="ml-64 flex-grow p-8">
        
        {/* VIEW POST MODAL (ADMIN VIEW) */}
        {viewPost && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
            <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col animate-in fade-in zoom-in duration-300">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Admin Post Preview</h2>
                  <p className="text-sm text-gray-500">Post ID: {viewPost.id}</p>
                </div>
                <button 
                  onClick={() => setViewPost(null)}
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="overflow-y-auto p-8 flex-grow">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  {/* Left: Images & Basic Info */}
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-3">
                      {viewPost.images.map((img, i) => (
                        <img key={i} src={img} className="w-full h-48 object-cover rounded-2xl border border-gray-100" alt="" />
                      ))}
                    </div>
                    <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
                      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Tag size={18} className="text-green-600" />
                        Item Details
                      </h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Price</p>
                          <p className="font-bold text-green-600 text-lg">{Number(viewPost.price).toLocaleString()} ETB</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Condition</p>
                          <p className="font-bold">{viewPost.condition}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-500">Location</p>
                          <p className="font-bold flex items-center gap-1"><MapPin size={14} /> {viewPost.location}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Meta & Seller Info */}
                  <div className="space-y-6">
                    <div>
                      <h1 className="text-3xl font-bold text-gray-900 mb-2">{viewPost.title}</h1>
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                        <Calendar size={14} />
                        Posted on {new Date(viewPost.created_at).toLocaleString()}
                      </div>
                      <p className="text-gray-600 leading-relaxed bg-blue-50/30 p-4 rounded-xl border border-blue-50 italic">
                        "{viewPost.description}"
                      </p>
                    </div>

                    <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <ShieldCheck size={18} className="text-blue-600" />
                        Seller Information
                      </h3>
                      <div className="space-y-3 text-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center font-bold text-blue-600">
                            {viewPost.userName ? viewPost.userName[0] : "U"}
                          </div>
                          <div>
                            <p className="font-bold">{viewPost.userName}</p>
                            <p className="text-xs text-gray-500">Seller ID: {viewPost.user_id}</p>
                          </div>
                        </div>
                        <p className="flex items-center gap-2 text-gray-600"><Mail size={14} /> {viewPost.userEmail}</p>
                        <p className="flex items-center gap-2 text-gray-600"><Phone size={14} /> {viewPost.userPhone}</p>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                      <h3 className="font-bold text-gray-900 mb-4">Specifications</h3>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        {Object.entries(viewPost.meta).map(([k, v]) => (
                          <div key={k} className="flex justify-between border-b border-gray-50 py-1">
                            <span className="text-gray-500 text-xs">{k}:</span>
                            <span className="font-bold text-xs">{v}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                <button 
                  onClick={() => handleMessageSeller(viewPost.id, viewPost.user_id)}
                  className="px-6 py-2 bg-blue-100 text-blue-700 rounded-xl font-bold hover:bg-blue-200 transition-colors flex items-center gap-2"
                >
                  <MessageSquare size={18} />
                  Message Seller
                </button>
                <button 
                  onClick={() => setViewPost(null)}
                  className="px-6 py-2 bg-white border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                {!viewPost.is_hidden ? (
                  <button 
                    onClick={() => {
                      handleDelete(viewPost.id);
                      setViewPost(null);
                    }}
                    className="px-6 py-2 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-colors shadow-lg shadow-red-100"
                  >
                    Hide Post
                  </button>
                ) : (
                  <button 
                    onClick={() => handleRepost(viewPost.id)}
                    className="px-6 py-2 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-colors shadow-lg shadow-green-100 flex items-center gap-2"
                  >
                    <RefreshCcw size={18} />
                    Repost Item
                  </button>
                )}
              </div>
            </div>
          </div>
        )}


        <header className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manage Posts</h1>
            <p className="text-gray-500">Review and moderate user listings.</p>
          </div>
          <div className="flex gap-4">
            <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100 flex items-center gap-2">
              <ShieldCheck className="text-green-500" size={18} />
              <span className="text-sm font-bold text-gray-700">
                {posts.filter(p => p.is_hidden === 0).length} Active
              </span>
            </div>
            <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100 flex items-center gap-2">
              <Clock className="text-red-500" size={18} />
              <span className="text-sm font-bold text-gray-700">
                {posts.filter(p => p.is_hidden === 1).length} Hidden
              </span>
            </div>
          </div>
        </header>

        {loading ? (
          <div className="text-gray-500">Loading posts...</div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-100 text-gray-600 text-sm uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Post</th>
                    <th className="px-6 py-4 font-semibold">Seller</th>
                    <th className="px-6 py-4 font-semibold">Price</th>
                    <th className="px-6 py-4 font-semibold">Status</th>
                    <th className="px-6 py-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {posts.map((post) => (
                    <tr key={post.id} className={`hover:bg-gray-50 transition-colors ${post.is_hidden ? "bg-gray-50/50" : ""}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img 
                            src={post.Image ? `${apiUrl}/uploads/posts/${post.Image}` : "/placeholder.jpg"} 
                            className={`w-12 h-12 rounded-lg object-cover bg-gray-100 ${post.is_hidden ? "grayscale opacity-50" : ""}`}
                            alt="" 
                          />
                          <div>
                            <p className={`font-bold text-gray-900 line-clamp-1 ${post.is_hidden ? "text-gray-400 line-through" : ""}`}>{post.title}</p>
                            <p className="text-xs text-gray-500">{new Date(post.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">{post.userName}</p>
                        <p className="text-xs text-gray-500">{post.userEmail}</p>
                      </td>
                      <td className="px-6 py-4 font-bold text-green-600">
                        {Number(post.price)?.toLocaleString()} ETB
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          post.is_hidden 
                            ? "bg-red-100 text-red-700" 
                            : "bg-green-100 text-green-700"
                        }`}>
                          {post.is_hidden ? 'Hidden' : 'Live'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button 
                            onClick={() => handleMessageSeller(post.id, post.user_id)}
                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                            title="Message Seller"
                          >
                            <MessageSquare size={18} />
                          </button>
                          <button 
                            onClick={() => handleView(post.id)}
                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                            title="Admin View"
                          >
                            <Eye size={18} />
                          </button>
                          {post.is_hidden ? (
                            <button 
                              onClick={() => handleRepost(post.id)}
                              className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                              title="Repost"
                            >
                              <RefreshCcw size={18} />
                            </button>
                          ) : (
                            <button 
                              onClick={() => handleDelete(post.id)}
                              className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                              title="Hide"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
